import {uniq} from 'lodash';

import {globalWindow} from '../../../../libs/shared/helpers/src/browser/browserHelpers';
import {tryParseUrl} from '../../../../libs/shared/helpers/src/browser/urlHelpers';
import {ExtendedError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {
  EntryPointTypesEnum,
  HttpRequest,
  HttpResponse,
} from '../../../applications/bridge/v2/types/bridgeTypesV2';
import {HttpRelayRequest} from '../../../applications/bridge/v2/types/internal/relayTypesV2';
import {CancelToken} from '../../../helpers/asyncCancelHelpers';
import {findHttpRequestMethodFromHttpVerb} from '../../../helpers/models/legacyApplicationHelpers';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {ApiApplicationAuthorizationUrl} from '../../../models/api/applicationAuthorizationUrlModel';
import {ApplicationPreferencesModel} from '../../../models/api/applicationPreferencesModel';
import {LegacyApplicationModel} from '../../../models/api/legacyApplicationModel';
import {InboxesSearchLocationsEnum} from '../../../models/url/inboxesUrlModel';
import {postApiAsync} from '../../../services/http/http';
import {RequestMethodsEnum, sendAsync} from '../../../services/http/httpBase';
import {HttpResponseError} from '../../../services/http/httpError';
import {batchStore} from '../../appBatching';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {getHasNewIntegrationsDiscoverability} from '../../featureFlags/featureFlagsSelectors';
import {showApplicationEnabledBannerAction} from '../../panels/app/banners/bannersActions';
import {getOptionalFirstAppPanelId} from '../../panels/panelsSelectors';
import {AppPanelId} from '../../panels/panelStateBase';
import {searchByParametersAction} from '../../stateless/searchActions';
import {updateEnabledLegacyApplicationIdsAction} from '../localPreferences/localPreferencesActions';
import {getLocalPreferences} from '../localPreferences/localPreferencesSelectors';
import {legacyApplicationOpenUrlInPopupAction} from './legacyApplicationsBrowserActions';
import {getFlatLegacyApplications, getLegacyApplications} from './legacyApplicationsSelectors';

/*
 * Action types.
 */

export enum LegacyApplicationsActionsEnum {
  APPLICATION_UPDATED = '@@data/applications/APPLICATION_UPDATED',
  APPLICATION_DELETED = '@@data/applications/APPLICATION_DELETED',
}

/*
 * Types.
 */

interface LegacyApplicationUpdatedAction extends ActionWithPayload<LegacyApplicationModel> {
  type: LegacyApplicationsActionsEnum.APPLICATION_UPDATED;
}
interface LegacyApplicationDeletedAction extends ActionWithPayload<string> {
  type: LegacyApplicationsActionsEnum.APPLICATION_DELETED;
}

/*
 * Action creators.
 */

export function updateLegacyApplication(newApplication: LegacyApplicationModel): ThunkAction {
  return (dispatch, getState) => {
    // Find the existing application, and check if it should be updated.
    const existingApplication = getState().data.legacyApplications[newApplication.id];
    if (existingApplication && existingApplication.updatedAt > newApplication.updatedAt) {
      return;
    }

    if (
      !existingApplication?.isEnabled &&
      newApplication.isEnabled &&
      newApplication.entryPoints.length > 0 &&
      getHasNewIntegrationsDiscoverability(getState())
    ) {
      const appPanelId = getOptionalFirstAppPanelId(getState());

      // Make sure the application has a side_panel entry point otherwise do not dispatch the banner.
      const hasPluginEntrypoint = newApplication.entryPoints.some(
        (e) => e.type === EntryPointTypesEnum.SIDE_PANEL,
      );

      if (appPanelId && hasPluginEntrypoint) {
        dispatch(showApplicationEnabledBannerAction(appPanelId, undefined, newApplication));
      }
    }

    const mergedNamespacePreferences = {
      ...(existingApplication && existingApplication.namespacePreferences),
      global: newApplication.namespacePreferences.global,
    };

    const action: LegacyApplicationUpdatedAction = {
      type: LegacyApplicationsActionsEnum.APPLICATION_UPDATED,
      payload: {
        ...existingApplication,
        ...newApplication,
        namespacePreferences: mergedNamespacePreferences,
      },
    };

    // Get all applications and track the newly enabled application ids and store it in local prefs.
    const applications = getFlatLegacyApplications(getState());
    const enabledApplicationIds = uniq(
      [...applications, newApplication].filter((a) => a.isEnabled).map((a) => a.id),
    );
    batchStore(() => {
      dispatch(action);
      dispatch(updateEnabledLegacyApplicationIdsAction(enabledApplicationIds));
    });
  };
}

export function updateLegacyApplicationPreferences(
  applicationId: number,
  newPreferences: ApplicationPreferencesModel,
): ThunkAction {
  return (dispatch, getState) => {
    // Find the application to update.
    const application = getState().data.legacyApplications[applicationId];
    if (!application) {
      return null;
    }

    // Check if the preferences should be updated.
    const existingPreferences = application.namespacePreferences[newPreferences.namespace];
    if (existingPreferences && existingPreferences.updatedAt > newPreferences.updatedAt) {
      return null;
    }

    const mergedPreferences = {
      ...application.namespacePreferences,
      [newPreferences.namespace]: newPreferences,
    };

    const action: LegacyApplicationUpdatedAction = {
      type: LegacyApplicationsActionsEnum.APPLICATION_UPDATED,
      payload: {...application, namespacePreferences: mergedPreferences},
    };
    return dispatch(action);
  };
}

export function deleteLegacyApplication(applicationId: string): ThunkAction {
  return (dispatch, getState) => {
    // Find the existing application, and check if it should be updated.
    const existingApplication = getState().data.legacyApplications[applicationId];

    if (!existingApplication) {
      return null;
    }

    const action: LegacyApplicationDeletedAction = {
      type: LegacyApplicationsActionsEnum.APPLICATION_DELETED,
      payload: applicationId,
    };

    return dispatch(action);
  };
}

export function sendLegacyApplicationHttpRequestAsyncAction(
  applicationId: number,
  request: HttpRequest,
  cancelToken: CancelToken | undefined,
): ThunkAsyncAction<HttpResponse> {
  return async (_dispatch, getState) => {
    const application = getLegacyApplications(getState())[applicationId];
    if (!application || !application.baseUrl) {
      throw new Error('This application does not support forwarding HTTP requests.');
    }

    try {
      const response = await sendAsync({
        method: findHttpRequestMethodFromHttpVerb(request.verb),
        path: application.baseUrl + request.path,
        body: request.body,
        cancelToken,
      });

      return {
        headers: {},
        status: response.status,
        body: response.data,
      };
    } catch (err) {
      if (!(err instanceof HttpResponseError)) {
        throw err;
      }

      return {
        headers: {},
        status: err.status,
        body: err.body,
      };
    }
  };
}

export function relayLegacyApplicationHttpRequestAsyncAction(
  applicationId: number,
  request: HttpRelayRequest,
  cancelToken: CancelToken | undefined,
): ThunkAsyncAction<HttpResponse> {
  return async (_dispatch, getState) => {
    const application = getLegacyApplications(getState())[applicationId];
    if (!application) {
      throw new Error('This application does not support relaying HTTP requests.');
    }

    try {
      const response = await sendAsync({
        method: RequestMethodsEnum.POST,
        path: `${application.url}/relay`,
        body: request,
        cancelToken,
      });

      return response.data as HttpResponse;
    } catch (err) {
      if (!(err instanceof HttpResponseError)) {
        throw err;
      }

      throw new ExtendedError('Front was not able to relay this request.', err);
    }
  };
}

export function authenticateLegacyApplicationAction(applicationId: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const application = getLegacyApplications(getState())[applicationId];
    if (!application || !application.authenticationUrl) {
      throw new Error('This application does not support authentication.');
    }

    const computedAbsoluteUrl = tryParseUrl(application.authenticationUrl, globalWindow.location.href);
    if (!computedAbsoluteUrl) {
      throw new Error('Unable to authenticate application.');
    }

    try {
      const authorizationUrl = await postApiAsync(
        ApiApplicationAuthorizationUrl,
        computedAbsoluteUrl.pathname,
        {},
      );

      const loginUrl = authorizationUrl.url;

      if (!loginUrl) {
        throw new Error('Unable to get an authorization URL.');
      }

      return dispatch(legacyApplicationOpenUrlInPopupAction(applicationId, loginUrl));
    } catch (err) {
      if (!(err instanceof HttpResponseError)) {
        throw err;
      }

      throw new ExtendedError('Failed to get an authorization URL.', err);
    }
  };
}

export function deauthenticateLegacyApplicationAction(applicationId: number): ThunkAsyncAction {
  return async (_dispatch, getState) => {
    const application = getLegacyApplications(getState())[applicationId];
    if (!application || !application.authenticationUrl) {
      throw new Error('This application does not support authentication.');
    }

    await sendAsync({
      method: RequestMethodsEnum.DELETE,
      path: `${application.url}/credentials`,
    });
  };
}

export function applicationSearchAction(query: string) {
  return searchByParametersAction({
    location: InboxesSearchLocationsEnum.GLOBAL,
    query,
  });
}

export function updateEnabledApplicationsAndDisplayBannersAction(panelId: AppPanelId): ThunkAction {
  return (dispatch, getState) => {
    const applications = getFlatLegacyApplications(getState());
    const storedEnabledApplicationIds = getLocalPreferences(getState()).enabledLegacyApplicationIds;
    const hasNewIntegrationsDiscoverability = getHasNewIntegrationsDiscoverability(getState());

    // Filter only enabled applications.
    const enabledApplications = [...applications].filter((a) => a.isEnabled);

    // If we have no stored application ids we can assume this is coming from a clean boot.
    // When we have a clean boot, we can't show the banners since it could show applications
    // that have already been enabled.
    // TODO: Also if they are not on the feature flag we can short-circut this. Remove when feature flag shipped.
    if (storedEnabledApplicationIds.length === 0 || !hasNewIntegrationsDiscoverability) {
      dispatch(updateEnabledLegacyApplicationIdsAction([...enabledApplications].map((a) => a.id)));
      return;
    }

    batchStore(() => {
      // Loop through all the currently enabled applications.
      for (const application of enabledApplications) {
        // If the application is already enabled, we do not need to do anything.
        if (storedEnabledApplicationIds.includes(application.id)) {
          continue;
        }

        // Make sure the application has a side_panel entry point otherwise do not dispatch the banner.
        const hasPluginEntrypoint = application.entryPoints.some(
          (e) => e.type === EntryPointTypesEnum.SIDE_PANEL,
        );
        if (!hasPluginEntrypoint) {
          continue;
        }

        dispatch(showApplicationEnabledBannerAction(panelId, undefined, application));
      }
      // Update the enabled application ids.
      dispatch(updateEnabledLegacyApplicationIdsAction([...enabledApplications].map((a) => a.id)));
    });
  };
}

/*
 * Union type.
 */

export type LegacyApplicationsAction = LegacyApplicationUpdatedAction | LegacyApplicationDeletedAction;
